/**
 * MoveToPositionAction - behavior tree action for moving to a position.
 */

import { NpcMovementComponent } from '../../../../../components/movement/NpcMovementComponent.js';
import { EngineClass } from '../../../../../systems/ClassRegistry.js';
import { BehaviorStatus } from '../../../BehaviorNode.js';
import { BehaviorAction, type BehaviorActionOptions } from '../../BehaviorAction.js';

import type { Blackboard } from '../../../blackboard/Blackboard.js';
import type * as THREE from 'three';

export interface MoveToPositionActionOptions extends BehaviorActionOptions {
  targetPosition?: THREE.Vector3;
  targetPositionKey?: string;
  acceptanceRadius?: number;
  useNavigation?: boolean;
}

@EngineClass('MoveToPositionAction')
export class MoveToPositionAction extends BehaviorAction {
  private targetPosition?: THREE.Vector3;
  private targetPositionKey?: string;
  private acceptanceRadius: number;
  private useNavigation: boolean;
  private movementComponent: NpcMovementComponent | null = null;

  constructor(options: MoveToPositionActionOptions = {}) {
    super({
      name: 'MoveTo',
      ...options
    });

    this.targetPosition = options.targetPosition;
    this.targetPositionKey = options.targetPositionKey;
    this.acceptanceRadius = options.acceptanceRadius ?? 2.0;
    this.useNavigation = options.useNavigation ?? true;
  }

  protected override onInitialize(blackboard: Blackboard): void {
    const owner = this.getOwner(blackboard);
    if (!owner) {
      return;
    }

    this.movementComponent = owner.getComponent(NpcMovementComponent);
    if (!this.movementComponent) {
      console.warn('MoveToPositionAction: No NpcMovementComponent found on actor');
      return;
    }
  }

  protected override onEnter(blackboard: Blackboard): void {
    const owner = this.getOwner(blackboard);
    if (!owner || !this.movementComponent) {
      return;
    }

    // Determine target position
    let targetPos: THREE.Vector3 | undefined;
    if (this.targetPositionKey) {
      targetPos = blackboard.getValue<THREE.Vector3>(this.targetPositionKey);
    } else if (this.targetPosition) {
      targetPos = this.targetPosition;
    }

    if (!targetPos) {
      console.warn('MoveToPositionAction: No target position specified');
      return;
    }

    // Use the movement component's new method - it handles all pathfinding internally
    this.movementComponent.setTargetPosition(targetPos, this.acceptanceRadius);
  }

  protected override async onUpdate(blackboard: Blackboard, deltaTime: number): Promise<BehaviorStatus> {
    const owner = this.getOwner(blackboard);
    if (!owner || !this.movementComponent) {
      return BehaviorStatus.Failure;
    }

    // Let the movement component handle all the logic - just check if it has reached the target
    if (this.movementComponent.hasReachedCurrentTarget()) {
      return BehaviorStatus.Success;
    }

    return BehaviorStatus.Running;
  }

  protected override onExit(blackboard: Blackboard, status: BehaviorStatus): void {
    // Only stop movement if we're exiting due to failure or cancellation
    // On success, let the movement component naturally complete
    if (status === BehaviorStatus.Failure) {
      this.movementComponent?.stopMovement();
    }
  }
}
